import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput } from '../_fitnessHelpers';
import { CycleCalendar } from './_pregnancyHelpers';

const fmt = d => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const fmtLong = d => d.toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });

export default function PeriodCalc() {
  const today = new Date().toISOString().slice(0,10);
  const [lmp, setLmp] = useState(today);
  const [cycle, setCycle] = useState(28);
  const [duration, setDuration] = useState(5);
  const clear = () => { setLmp(today); setCycle(28); setDuration(5); };

  const c = useMemo(() => {
    const s = new Date(lmp); if (isNaN(s)) return null;
    const list = [];
    for (let i = 1; i <= 12; i++) {
      const start = new Date(s.getTime() + i * cycle * 86400000);
      const end = new Date(start.getTime() + (duration - 1) * 86400000);
      const ov = new Date(start.getTime() - 14 * 86400000);
      list.push({ start, end, ov });
    }
    return list;
  }, [lmp, cycle, duration]);

  return (
    <CalcShell look="cnet" title="Period Calculator" subtitle="Predict the timing of the next 12 menstrual cycles based on inputs.">
      <CalcNetPanel
        slug="pd"
        tabs={['Period']}
        activeTab={0}
        onTabChange={() => {}}
        onClear={clear}
        right={c && (
          <div>
            <div className="text-lg text-slate-800">Next Period</div>
            <div className="text-xl font-bold text-[#2f9c3f]" data-testid="pd-next">{fmtLong(c[0].start)}</div>
            <p className="text-sm text-slate-600 mt-1">Ends around {fmtLong(c[0].end)}</p>
            <div className="mt-4">
              <table className="w-full text-sm border-collapse" data-testid="pd-table">
                <thead>
                  <tr>
                    <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Cycle #</th>
                    <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Period Start</th>
                    <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Period End</th>
                    <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Ovulation</th>
                  </tr>
                </thead>
                <tbody>
                  {c.map((p, i) => (
                    <tr key={i} className={i%2?'bg-slate-50':''}>
                      <td className="border px-3 py-1.5">{i + 1}</td>
                      <td className="border px-3 py-1.5">{fmt(p.start)}</td>
                      <td className="border px-3 py-1.5">{fmt(p.end)}</td>
                      <td className="border px-3 py-1.5">{fmt(p.ov)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      >
        <Row label="First day of your last period">
          <input type="date" value={lmp} onChange={e => setLmp(e.target.value)} data-testid="pd-lmp"
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
        </Row>
        <Row label="Cycle length"><UnitInput value={cycle} onChange={setCycle} unit="days" testid="pd-cycle" /></Row>
        <Row label="Period duration"><UnitInput value={duration} onChange={setDuration} unit="days" testid="pd-duration" /></Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Each following period start ≈ previous period + cycle length (${cycle} days)`,
        `Ovulation ≈ next period − 14 days`,
      ]} />
      <CycleCalendar lmpDate={new Date(lmp)} cycleLen={cycle} testId="pd-calendar" />
    </CalcShell>
  );
}
